import React, { useEffect } from "react";
import { cls } from "@wss/cls";
import { addBreadcrumb } from "@wss/error-tracking";
import formatAsCurrency from "@wss/utilities/formatAsCurrency";
import { EnrollmentOption } from "../../Models";
const freeMonthEnrollmentOptionName = "First month FREE";
interface Props {
  enrollmentOptions: EnrollmentOption[];
  formattedPlusPrice: string;
  setPromotionName: (val: string | undefined) => void;
  promotionName: string | undefined;
  isFreeview?: boolean;
  subscriptionChargeDate?: Date;
}
const WebPlusPaymentDetails = ({
  enrollmentOptions,
  formattedPlusPrice,
  setPromotionName,
  promotionName,
  isFreeview = false,
  subscriptionChargeDate
}: Props) => {
  const getFormattedPrice = (enrollmentOption: EnrollmentOption | undefined) => {
    if (enrollmentOption === undefined) return formattedPlusPrice;
    const price = enrollmentOption.price ?? 0;
    const formattedPrice = formatAsCurrency.formatIfUndefined(price);
    return formattedPrice.replace(".00", "");
  };
  useEffect(() => {
    if (!enrollmentOptions || enrollmentOptions.length === 0) {
      addBreadcrumb({
        message: `In WebPlusPaymentDetails with empty enrollmentOptions. This shouldn't happen, and will likely cause errors.`,
        data: {
          promotionName,
          subscriptionChargeDate,
          isFreeview,
          formattedPlusPrice
        }
      }, "warning");
    }
  });
  useEffect(() => {
    if (isFreeview) {
      const freeMonthEnrollmentOption = enrollmentOptions.find(e => e.promotionName === freeMonthEnrollmentOptionName);
      if (freeMonthEnrollmentOption) {
        const index = enrollmentOptions.indexOf(freeMonthEnrollmentOption);
        enrollmentOptions.splice(index, 1);
      }
    }
  }, [isFreeview, enrollmentOptions]);
  return <div className="px-0 py-6 border-0 border-b border-solid border-gray-200" data-sentry-component="WebPlusPaymentDetails" data-sentry-source-file="index.tsx">
      <fieldset aria-labelledby="plus-modal-payment-section-heading" className="p-0 border-0">
        <div className="flex justify-between">
          <span id="plus-modal-payment-section-heading" className="w-auto pb-2 text-base font-bold text-gray-800">
            Payment
          </span>
          <p className="leading-none pb-2 mb-0">
            <span className="leading-none text-sm font-normal" aria-live="polite" aria-atomic="true">
              {isFreeview && subscriptionChargeDate ? `Total due on ${new Date(subscriptionChargeDate).toLocaleDateString("en-US")}: ` : "Total due today: "}
              <span className="font-semibold" data-component="webplusEnrollmentCharge">
                {getFormattedPrice(enrollmentOptions.find(e => e.promotionName === promotionName))}
              </span>
            </span>
          </p>
        </div>
        <div className="w-full grid grid-cols-2 gap-x-2">
          {enrollmentOptions.length > 1 && enrollmentOptions.map(option => {
          return <label key={option.promotionName} className={cls("inline-flex text-center w-auto py-2.5 justify-center items-center cursor-pointer", "text-gray-800 rounded text-base font-bold", "border-2 border-gray-600 border-solid", {
            "bg-green-50 border-green-500": promotionName === option.promotionName,
            "hover:bg-gray-100": promotionName !== option.promotionName
          })} tabIndex={0} // Make label focusable since input is hidden
          onKeyDown={e => {
            if (["ArrowLeft", "ArrowRight"].includes(e.key)) {
              setPromotionName(option.promotionName); // Updates selection immediately to prevent needing 2 tabs to change selection
              e.currentTarget.querySelector("input")?.focus(); // Moves focus to the radio input for proper keyboard navigation
            }
          }}>
                  <input className="sr-only" type="radio" name="promotionId" value={option.promotionName ?? ""} data-charge={getFormattedPrice(option)} data-radio-toggle-target="webplusEnrollmentCharge" onChange={() => setPromotionName(option.promotionName)} checked={promotionName === option.promotionName} tabIndex={-1} // prevent hidden input from interfereing with focus of label
            />
                  <b className={cls("font-bold", "m-0", "text-sm", "xsl:text-base", "leading-4", promotionName === option.promotionName && "text-green-500")}>
                    {option.promotionName}
                  </b>
                </label>;
        })}
        </div>
        {enrollmentOptions.length === 1 && <span className={cls("text-base", "font-normal", "text-gray-800", "leading-none")}>
            {enrollmentOptions[0]?.promotionName}
          </span>}
      </fieldset>
    </div>;
};
export default WebPlusPaymentDetails;